import { useMemo, useRef, useState } from 'react';
import {
  getDdjj,
  guardarDdjj,
  subirArchivoDdjj,
  eliminarArchivoDdjj,
  chequearEmailEnUso,
  type DdjjIntegrante,
  type DdjjPregunta,
  type DdjjRespuestaValor,
} from '@lib/services/portal.service';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { capitalizeFullName } from '@lib/utils/text';
import { MedicusDropdown } from '@components/medicus-ui/MedicusDropdown';
import { EmailEnUsoModal } from './EmailEnUsoModal';

// Fallback por si el server no devuelve mensaje (debería devolverlo siempre).
const EMAIL_EN_USO_FALLBACK =
  'Este email ya está siendo usado por un socio de Medicus. Ingresá uno distinto para continuar.';

/** Formatea un tamaño en bytes a "X KB" o "X MB" con un decimal. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Etiqueta humana del parentesco para el badge del integrante. Si el código
// no está mapeado, caemos al "Parentesco N" crudo.
const PARENTESCO_LABEL: Record<string, string> = {
  '0': 'Titular',
  '5': 'Cónyuge',
  '7': 'Hijo/a',
  '13': 'Adherente',
};

// Helpers de validación de tipos compuestos del DDJJ. Extraídos para bajar
// la cognitive-complexity de `isAnswered` (Sonar) sin perder semántica.
function checkBoolText(v: string, text: string | undefined): boolean {
  if (v === 'no') return true;
  if (v !== 'si') return false;
  return !!(text && text.trim().length > 0);
}

function checkBoolText2(
  v: string,
  text: string | undefined,
  text2: string | undefined,
): boolean {
  if (v === 'no') return true;
  if (v !== 'si') return false;
  return (text ?? '').trim().length > 0 && (text2 ?? '').trim().length > 0;
}

interface Props {
  token: string;
  onBack: () => void;
  onContinue: () => void;
  /**
   * Modo activación de un legajo_bis (Fase 2 alta-directa): rutea la DDJJ a los
   * endpoints `/portal-bis/:token/ddjj` (solo los integrantes nuevos) y saltea
   * el chequeo de email en uso (no aplica: el integrante nuevo no es titular).
   * Los adjuntos siguen usando los endpoints compartidos de archivos-ddjj.
   */
  bis?: boolean;
}

export function DdjjStep({ token, onBack, onContinue, bis }: Props) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['ddjj', token],
    queryFn: () => getDdjj(token, { bis }),
    enabled: !!token,
  });

  // Antes de avanzar al plan validamos que el email de contacto del titular no
  // esté siendo usado por otro socio ya dado de alta. Si lo está, abrimos un
  // modal para ingresar uno nuevo. Lo chequeamos acá (y no solo en el paso del
  // titular) porque ~15.9k titulares ya pasaron ese paso antes de esta validación.
  // OJO hooks: estos van ANTES de los early-returns de loading/error.
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailMensaje, setEmailMensaje] = useState('');

  const checkEmailMut = useMutation({
    mutationFn: () => chequearEmailEnUso(token),
    onSuccess: (r) => {
      if (r.enUso) {
        setEmailMensaje(r.mensaje ?? EMAIL_EN_USO_FALLBACK);
        setEmailModalOpen(true);
      } else {
        onContinue();
      }
    },
    // Si el chequeo falla (red/server), no bloqueamos: dejamos avanzar y que el
    // gate del server (patch/finalizar) actúe como red de seguridad.
    onError: () => onContinue(),
  });

  if (q.isLoading) {
    return <p className="text-element-mid">Cargando preguntas…</p>;
  }
  if (q.isError || !q.data) {
    return (
      <p className="status status-error" style={{ display: 'block' }}>
        No se pudo cargar el cuestionario de salud.
      </p>
    );
  }

  const ddjj = q.data;
  const todasCompletas = ddjj.integrantes.every((i) => i.estado === 'completada');

  return (
    <div className="onboarding__form">
      <p className="text-element-mid" style={{ fontSize: 14, marginBottom: 8 }}>
        Te vamos a hacer algunas preguntas sobre tu salud para poder cuidarte mejor desde el primer
        día, y conocer qué tratamientos tenés en curso. Esta información no va a condicionar tu
        ingreso pero es vital para obtener tu credencial digital. Bienvenido a MEDICUS.
      </p>

      {ddjj.integrantes.map((integ) => (
        <IntegranteDdjj
          key={integ.asociadoId}
          token={token}
          catalogo={ddjj.catalogo}
          integrante={integ}
          bis={bis}
          onSaved={() => qc.invalidateQueries({ queryKey: ['ddjj', token] })}
        />
      ))}

      <div className="onboarding__actions" style={{ marginTop: 16 }}>
        <button className="btn btn-ghost" onClick={onBack}>Volver</button>
        <button
          className="btn btn-primary"
          onClick={() => (bis ? onContinue() : checkEmailMut.mutate())}
          disabled={!todasCompletas || checkEmailMut.isPending}
          title={todasCompletas ? '' : 'Completá el cuestionario de todos los integrantes'}
        >
          <span className="btn-font-style">
            {checkEmailMut.isPending
              ? 'Validando…'
              : todasCompletas
                ? 'Finalizar mi validación'
                : 'Completá todos los cuestionarios'}
          </span>
        </button>
      </div>

      <EmailEnUsoModal
        token={token}
        open={emailModalOpen}
        mensaje={emailMensaje}
        onResuelto={() => {
          setEmailModalOpen(false);
          onContinue();
        }}
        onCancelar={() => setEmailModalOpen(false)}
      />
    </div>
  );
}

function IntegranteDdjj({
  token,
  catalogo,
  integrante,
  onSaved,
  bis,
}: {
  token: string;
  catalogo: DdjjPregunta[];
  integrante: DdjjIntegrante;
  onSaved: () => void;
  bis?: boolean;
}) {
  const [open, setOpen] = useState(integrante.estado !== 'completada');

  const aplicables = useMemo(
    () => new Set(integrante.preguntasAplicables),
    [integrante.preguntasAplicables],
  );

  // Estado inicial: precargamos bool / bool+text / bool+text2 a "no" SOLO en
  // DDJJ realmente nuevas (sin ninguna respuesta guardada todavía). Si se
  // reabre/edita una DDJJ que ya tenía respuestas, NO autocompletamos las
  // preguntas nuevas del catálogo: el titular debe contestarlas explícitamente
  // (evita 'no' implícitos en la declaración jurada que el titular nunca eligió,
  // p.ej. tras un cambio de catálogo).
  const [respuestas, setRespuestas] = useState<Record<string, DdjjRespuestaValor>>(() => {
    const base: Record<string, DdjjRespuestaValor> = { ...(integrante.respuestas ?? {}) };
    const esNueva = Object.keys(integrante.respuestas ?? {}).length === 0;
    if (esNueva) {
      for (const p of catalogo) {
        if (!aplicables.has(p.codigo)) continue;
        if (base[p.codigo]) continue; // ya tiene respuesta guardada
        if (p.tipo === 'bool' || p.tipo === 'bool+text' || p.tipo === 'bool+text2') {
          base[p.codigo] = { value: 'no' };
        }
      }
    }
    return base;
  });

  // Agrupar por sección manteniendo orden del catálogo
  const secciones = useMemo(() => {
    const map = new Map<number, { titulo: string; preguntas: DdjjPregunta[] }>();
    for (const p of catalogo) {
      if (!aplicables.has(p.codigo)) continue;
      if (!map.has(p.seccion)) {
        map.set(p.seccion, { titulo: p.seccionTitulo, preguntas: [] });
      }
      map.get(p.seccion)!.preguntas.push(p);
    }
    return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
  }, [catalogo, aplicables]);

  function setResp(
    codigo: string,
    value: string,
    text?: string,
    text2?: string,
    // 3 estados para `file`:
    //   - undefined → preservar (caso del wizard normal sin tocar archivos)
    //   - null      → BORRAR el archivo del state local (al "Quitar")
    //   - objeto    → setear el archivo recién subido
    file?: DdjjRespuestaValor['file'] | null,
  ) {
    setRespuestas((prev) => {
      const prevFile = prev[codigo]?.file;
      const nuevoFile = file === null ? undefined : file ?? prevFile;
      const next: Record<string, DdjjRespuestaValor> = {
        ...prev,
        [codigo]: { value, text, text2, file: nuevoFile },
      };
      // Si cambió el valor del padre, limpiar las hijas que dependían del valor
      // anterior, así no quedan respuestas "stale" ocultas en el state local
      // (el back también las descarta al guardar, esto es para la UI + el conteo).
      if (prev[codigo]?.value !== value) {
        for (const q of catalogo) {
          if (q.dependeDe?.codigo === codigo && q.dependeDe.valor !== value) {
            delete next[q.codigo];
          }
        }
      }
      return next;
    });
  }

  function shouldRender(p: DdjjPregunta): boolean {
    // Resolución TRANSITIVA de dependeDe: una pregunta se muestra solo si TODA
    // su cadena de ancestros se satisface (ej. enf.diabetes.bomba requiere
    // enf.diabetes.insulina='si' Y enf.diabetes='si').
    let dep = p.dependeDe;
    const visto = new Set<string>();
    while (dep) {
      if (respuestas[dep.codigo]?.value !== dep.valor) return false;
      if (visto.has(dep.codigo)) break;
      visto.add(dep.codigo);
      dep = catalogo.find((q) => q.codigo === dep!.codigo)?.dependeDe;
    }
    return true;
  }

  function isAnswered(p: DdjjPregunta): boolean {
    // Preguntas opcionales nunca exigen respuesta — cuentan como "respondidas"
    // aunque estén vacías.
    if (p.opcional) return true;
    const r = respuestas[p.codigo];
    // Tipo 'file': la respuesta está completa si hay un archivo subido. El
    // value no aplica acá (lo setea el endpoint del módulo archivos-ddjj).
    if (p.tipo === 'file') return !!r?.file?.fileId;
    const v = (r?.value ?? '').trim();
    if (!v) return false;
    switch (p.tipo) {
      case 'bool':
        return v === 'si' || v === 'no';
      case 'bool+text':
        return checkBoolText(v, r?.text);
      case 'bool+text2':
        return checkBoolText2(v, r?.text, r?.text2);
      case 'select':
        return (p.opciones ?? []).includes(v);
      case 'date':
        return /^\d{2}\/\d{2}\/\d{4}$/.test(v);
      default:
        return v.length > 0;
    }
  }

  const { totalExigidas, totalRespondidas } = useMemo(() => {
    const aplicablesArr = catalogo.filter((p) => aplicables.has(p.codigo));
    const exigidas = aplicablesArr.filter(shouldRender);
    const respondidas = exigidas.filter(isAnswered).length;
    return { totalExigidas: exigidas.length, totalRespondidas: respondidas };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogo, aplicables, respuestas]);

  const todasRespondidas = totalRespondidas >= totalExigidas;
  const [submitError, setSubmitError] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: (completar: boolean) =>
      guardarDdjj(token, integrante.asociadoId, respuestas, completar, { bis }),
    onSuccess: () => {
      setSubmitError(null);
      onSaved();
      setOpen(false);
    },
    onError: (err: unknown) => {
      const e = err as {
        response?: { data?: { error?: { message?: string } } };
      };
      setSubmitError(
        e.response?.data?.error?.message ??
          'No se pudo guardar el cuestionario. Revisá las respuestas.',
      );
    },
  });

  return (
    <article
      className="onboarding__familiarCard"
      style={{ borderColor: integrante.estado === 'completada' ? 'var(--color-success)' : undefined }}
    >
      <header className="onboarding__familiarHeader">
        <div>
          <span className="onboarding__familiarBadge">
            {integrante.esTitular
              ? 'Titular'
              : PARENTESCO_LABEL[integrante.parentesco ?? ''] ??
                `Parentesco ${integrante.parentesco ?? '—'}`}
          </span>
          <h3 className="onboarding__familiarName">
            {capitalizeFullName(integrante.nombres, integrante.apellSolt)}
          </h3>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 6,
          }}
        >
          <span
            className={
              integrante.estado === 'completada'
                ? 'status status-success'
                : 'status status-warning'
            }
          >
            {integrante.estado === 'completada' ? 'Completada' : 'En curso'}
          </span>
          <button
            className="btn btn-link"
            onClick={() => setOpen((v) => !v)}
            style={{ fontSize: 13 }}
          >
            {open ? 'Cerrar' : integrante.estado === 'completada' ? 'Ver / editar' : 'Completar'}
          </button>
        </div>
      </header>

      {open && (
        <div className="onboarding__familiarBody">
          {secciones.map(([num, sec]) => (
            <fieldset key={num} className="onboarding__section" style={{ marginBottom: 8 }}>
              <legend className="onboarding__sectionTitle">
                {num}. {sec.titulo}
              </legend>
              <div className="onboarding__sectionBody">
                {sec.preguntas.filter(shouldRender).map((p) => (
                  <Pregunta
                    key={p.codigo}
                    pregunta={p}
                    respuesta={respuestas[p.codigo]}
                    onChange={(v, t, t2, f) => setResp(p.codigo, v, t, t2, f)}
                    token={token}
                    asociadoId={integrante.asociadoId}
                  />
                ))}
              </div>
            </fieldset>
          ))}

          {submitError && (
            <p
              className="status status-error"
              style={{ display: 'block', marginTop: 8 }}
            >
              {submitError}
            </p>
          )}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 8,
              marginTop: 8,
              flexWrap: 'wrap',
            }}
          >
            <p
              className="text-element-mid"
              style={{ fontSize: 13, margin: 0 }}
            >
              Respondidas <strong>{totalRespondidas}</strong> de{' '}
              <strong>{totalExigidas}</strong>
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn btn-ghost"
                onClick={() => saveMut.mutate(false)}
                disabled={saveMut.isPending}
              >
                Guardar borrador
              </button>
              <button
                className="btn btn-primary"
                onClick={() => saveMut.mutate(true)}
                disabled={saveMut.isPending || !todasRespondidas}
                title={
                  todasRespondidas
                    ? ''
                    : `Te faltan ${totalExigidas - totalRespondidas} respuesta(s)`
                }
              >
                <span className="btn-font-style">Marcar como completada</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </article>
  );
}

function Pregunta({
  pregunta,
  respuesta,
  onChange,
  token,
  asociadoId,
}: {
  pregunta: DdjjPregunta;
  respuesta?: DdjjRespuestaValor;
  // `file` admite `null` para forzar borrado del state local cuando se
  // hace click en "Quitar" (sino el undefined queda como "preservar" y
  // el nombre del archivo seguía visible hasta el próximo refresh).
  onChange: (
    value: string,
    text?: string,
    text2?: string,
    file?: DdjjRespuestaValor['file'] | null,
  ) => void;
  token: string;
  asociadoId: string;
}) {
  const v = respuesta?.value ?? '';
  const t = respuesta?.text ?? '';
  const t2 = respuesta?.text2 ?? '';

  return (
    <div className="onboarding__field" style={{ borderTop: '1px solid var(--color-outline)', paddingTop: 10 }}>
      <span className="onboarding__fieldLabel" style={{ fontSize: 13, color: 'var(--color-element-high)' }}>
        {pregunta.pregunta}
        {pregunta.opcional && (
          <small style={{ marginLeft: 6, color: 'var(--color-element-mid)', fontWeight: 400 }}>
            (opcional)
          </small>
        )}
      </span>

      {pregunta.tipo === 'file' && (
        <PreguntaArchivo
          pregunta={pregunta}
          file={respuesta?.file}
          token={token}
          asociadoId={asociadoId}
          onSubido={(meta) => onChange('si', undefined, undefined, meta)}
          onEliminado={() => onChange('', undefined, undefined, null)}
        />
      )}

      {pregunta.tipo === 'bool' && (
        <div style={{ display: 'flex', gap: 8 }}>
          <SiNoButton active={v === 'no'} onClick={() => onChange('no')}>No</SiNoButton>
          <SiNoButton active={v === 'si'} onClick={() => onChange('si')}>Sí</SiNoButton>
        </div>
      )}

      {pregunta.tipo === 'bool+text' && (
        <div>
          <div style={{ display: 'flex', gap: 8 }}>
            <SiNoButton active={v === 'no'} onClick={() => onChange('no')}>No</SiNoButton>
            <SiNoButton active={v === 'si'} onClick={() => onChange('si', t)}>Sí</SiNoButton>
          </div>
          {v === 'si' && (
            <input
              className="input"
              type="text"
              maxLength={1000}
              placeholder={pregunta.textLabel ?? 'Detalle'}
              value={t}
              onChange={(e) => onChange('si', e.target.value)}
              style={{ marginTop: 8 }}
            />
          )}
        </div>
      )}

      {pregunta.tipo === 'bool+text2' && (
        <div>
          <div style={{ display: 'flex', gap: 8 }}>
            <SiNoButton active={v === 'no'} onClick={() => onChange('no')}>No</SiNoButton>
            <SiNoButton active={v === 'si'} onClick={() => onChange('si', t, t2)}>Sí</SiNoButton>
          </div>
          {v === 'si' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
              <input
                className="input"
                type="text"
                maxLength={1000}
                placeholder={pregunta.textLabel ?? 'Especificar cuál/es'}
                value={t}
                onChange={(e) => onChange('si', e.target.value, t2)}
              />
              <input
                className="input"
                type="text"
                maxLength={1000}
                placeholder={pregunta.textLabel2 ?? 'Tratamiento'}
                value={t2}
                onChange={(e) => onChange('si', t, e.target.value)}
              />
            </div>
          )}
        </div>
      )}

      {pregunta.tipo === 'text' && (
        <input
          className="input"
          type="text"
          maxLength={200}
          value={v}
          onChange={(e) => onChange(e.target.value)}
        />
      )}

      {pregunta.tipo === 'date' && (
        <input
          className="input"
          type="text"
          placeholder="dd/mm/aaaa"
          value={v}
          onChange={(e) => onChange(e.target.value)}
        />
      )}

      {pregunta.tipo === 'select' && (
        <MedicusDropdown
          value={v}
          onChange={(val) => onChange(val)}
          options={(pregunta.opciones ?? []).map((o) => ({ value: o, label: o }))}
        />
      )}
    </div>
  );
}

/**
 * Render del input de archivo para preguntas tipo 'file'. Si no hay adjunto,
 * muestra el botón "Adjuntar". Si hay uno, muestra nombre + peso + botones
 * Reemplazar / Quitar. Valida mime y tamaño en el cliente antes de mandar al
 * server (que vuelve a validar igual — defense in depth).
 */
function PreguntaArchivo({
  pregunta,
  file,
  token,
  asociadoId,
  onSubido,
  onEliminado,
}: {
  pregunta: DdjjPregunta;
  file?: DdjjRespuestaValor['file'];
  token: string;
  asociadoId: string;
  onSubido: (meta: NonNullable<DdjjRespuestaValor['file']>) => void;
  onEliminado: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [err, setErr] = useState<string | null>(null);

  const mimes = pregunta.mimeAceptados ?? [];
  const maxMB = pregunta.tamMaxMB ?? 10;
  const acceptAttr = mimes.join(',');
  const ayudaFormatos = mimes
    .map((m) => m.split('/').pop()?.toUpperCase())
    .filter(Boolean)
    .join(' · ');

  const subirMut = useMutation({
    mutationFn: (f: File) => subirArchivoDdjj(token, asociadoId, pregunta.codigo, f),
    onSuccess: (meta) => {
      setErr(null);
      onSubido(meta);
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: { message?: string } } } };
      setErr(
        err.response?.data?.error?.message ??
          'No se pudo subir el archivo. Probá con otro o reducí el tamaño.',
      );
    },
  });

  const eliminarMut = useMutation({
    mutationFn: () => eliminarArchivoDdjj(token, asociadoId, pregunta.codigo),
    onSuccess: () => {
      setErr(null);
      onEliminado();
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: { message?: string } } } };
      setErr(err.response?.data?.error?.message ?? 'No se pudo eliminar el archivo.');
    },
  });

  function handleSeleccion(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = ''; // permite re-seleccionar el mismo archivo
    if (!f) return;
    if (mimes.length > 0 && !mimes.includes(f.type)) {
      setErr(`Formato no permitido. Aceptamos: ${ayudaFormatos}.`);
      return;
    }
    if (f.size > maxMB * 1024 * 1024) {
      setErr(`El archivo supera el límite de ${maxMB} MB (${formatBytes(f.size)}).`);
      return;
    }
    subirMut.mutate(f);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <input
        ref={inputRef}
        type="file"
        accept={acceptAttr || undefined}
        style={{ display: 'none' }}
        onChange={handleSeleccion}
      />

      {file?.fileId ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 12px',
            border: '1px solid var(--color-outline)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-medicus-celeste-bg-soft)',
            gap: 12,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-element-high)' }}>
              {file.name}
            </span>
            <span style={{ fontSize: 12, color: 'var(--color-element-mid)' }}>
              {formatBytes(file.size)}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button
              type="button"
              className="btn btn-ghost"
              style={{ fontSize: 13, padding: '6px 12px' }}
              disabled={subirMut.isPending || eliminarMut.isPending}
              onClick={() => inputRef.current?.click()}
            >
              Reemplazar
            </button>
            <button
              type="button"
              className="btn btn-link"
              style={{ fontSize: 13, color: 'var(--color-danger)' }}
              disabled={subirMut.isPending || eliminarMut.isPending}
              onClick={() => eliminarMut.mutate()}
            >
              {eliminarMut.isPending ? 'Quitando…' : 'Quitar'}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="btn btn-ghost"
          disabled={subirMut.isPending}
          onClick={() => inputRef.current?.click()}
          style={{
            alignSelf: 'flex-start',
            padding: '10px 18px',
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {subirMut.isPending ? 'Subiendo…' : 'Adjuntar archivo'}
        </button>
      )}

      {ayudaFormatos && (
        <small style={{ fontSize: 12, color: 'var(--color-element-mid)' }}>
          Formatos: {ayudaFormatos} · Máx {maxMB} MB
        </small>
      )}

      {err && (
        <p className="status status-error" style={{ display: 'block', margin: 0 }}>
          {err}
        </p>
      )}
    </div>
  );
}

function SiNoButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '8px 18px',
        borderRadius: 'var(--radius-md)',
        border: `1.5px solid ${active ? 'var(--color-primary-darker)' : 'var(--color-outline)'}`,
        background: active
          ? 'var(--color-medicus-celeste-bg-strong)'
          : 'var(--color-background)',
        color: 'var(--color-primary-darker)',
        fontWeight: active ? 700 : 600,
        fontSize: 14,
        cursor: 'pointer',
        transition: 'background 120ms, border-color 120ms',
      }}
    >
      {children}
    </button>
  );
}
